// @flow
import config from '../../../../config';
import { Platform, Dimensions} from 'react-native';
import ImageEditor from '@react-native-community/image-editor';
import RNFS from 'react-native-fs';
import { parse } from 'mrz';
import { createResizedImage } from './resizeImage';
import WizzeeLog from '../../../../lib/wizzeeLog';

export type ImageType = {
  uri?: string,
  data?: string,
  base64?: string,
  width?: number,
  height?: number,
};

let timeoutId;

export default async (image: ImageType) => {
  if (!image.base64) {
    throw Error('No image');
  }

  let croppedBase64Image;
  if (Platform.OS !== 'web') {
    croppedBase64Image = await cropImage(image);
  } else {
    croppedBase64Image = image.base64;
  }

  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      reject('Timeout');
    }, config.MAX_SCAN_TIME);
  }).finally(() => clearTimeout(timeoutId));

  const res = await Promise.race([fetchImageMrz(croppedBase64Image), timeout]);

  // For flow typing since reject should stop the execution
  if (!res) {
    throw Error();
  }

  const response: GCloudResponseType = await res.json();

  if (response.responses.length === 0) {
    throw Error('No result');
  }

  const mrzResult = extractMrzInformations(response.responses[0]?.fullTextAnnotation?.text);

  try {
    croppedBase64Image = await createResizedImage(croppedBase64Image);
  } catch (error) {
    WizzeeLog.warn(error);
  }

  return { mrzResult, base64Image: removeBase64Header(croppedBase64Image) };
};

const fetchImageMrz = (croppedBase64Image: string) =>
  fetch(
    `https://vision.googleapis.com/v1/images:annotate?key=${Platform.select({
      ios: config.GOOGLE_API_KEY_IOS,
      android: config.GOOGLE_API_KEY_ANDROID,
      web: config.GOOGLE_API_KEY_WEB,
    })}`,
    {
      method: 'POST',
      body: JSON.stringify({
        requests: [
          {
            image: {
              content: croppedBase64Image,
            },
            features: [
              {
                type: 'DOCUMENT_TEXT_DETECTION',
              },
            ],
          },
        ],
      }),
    }
  );

const removeBase64Header = (base64 = '') => {
  const base64WithoutHeader = base64.split(',')[1];
  return base64WithoutHeader || base64;
};

const cropImage = async (image: ImageType): Promise<string> => {
  if (!image.uri || !image.width || !image.height) {
    throw new Error('Photo not captured');
  }

  const { width: photoWidth, height: photoHeight, uri } = image;
  
  // 1. Get the screen width to match your overlay calculation logic
  const screenWidth = Dimensions.get('window').width;
  
  // 2. Calculate the scale ratio (Photo Pixels / Screen Points)
  const scale = photoWidth / screenWidth;

  // 3. Get the overlay dimensions in Screen Points
  const { overlayWidth, overlayHeight } = calculateOverlayDimensions(screenWidth);

  // 4. Scale those dimensions to Photo Pixels
  const scaledWidth = overlayWidth * scale;
  const scaledHeight = overlayHeight * scale;
  
  // 5. Calculate offsets in Photo Pixels
  const offsetY = (photoHeight / 2) - (scaledHeight / 2);
  const offsetX = (photoWidth - scaledWidth) / 2;

  const croppedUri = await ImageEditor.cropImage(uri, {
    offset: {
      x: offsetX,
      y: offsetY,
    },
    size: {
      width: scaledWidth,
      height: scaledHeight,
    },
    // Optional: displaySize allows you to downscale here to save memory/bandwidth
    // displaySize: { width: 1000, height: 700 } 
  });

  const base64image = await RNFS.readFile(croppedUri, 'base64');
  
  // Clean up the temporary cropped file
  try {
    await RNFS.unlink(croppedUri);
  } catch (e) {
    WizzeeLog.warn("Could not delete temp crop file", e);
  }

  return base64image;
};

const REPLACEMENT_CHARACTERS = ['', '<'];
const NB_REPLACEMENT_CHARACTERS = REPLACEMENT_CHARACTERS.length;
const LINE_RETURN_KEY = '\n';

export function calculateAllConcatenation(value: Array<string>): Array<Array<string>> {
  let result: Array<Array<string>> = [];
  for (var index = 0; index < NB_REPLACEMENT_CHARACTERS; index++) {
    result.push([...value, REPLACEMENT_CHARACTERS[index]]);
  }
  return result;
}

export function generateCombinations(nbBits: number): Array<Array<string>> {
  var result: Array<Array<string>> = [];

  for (var index = 0; index < nbBits; index++) {
    const ARRAY_LENGTH = result.length;
    const intermediateResult: Array<Array<string>> = [];

    if (ARRAY_LENGTH === 0) {
      intermediateResult.push(...calculateAllConcatenation([]));
    }

    for (var resultIndex = 0; resultIndex < ARRAY_LENGTH; resultIndex++) {
      // If we are in this loop it means that result[i] is defined
      // $FlowFixMe
      intermediateResult.push(...calculateAllConcatenation(result[resultIndex]));
    }

    result = intermediateResult;
  }

  return result;
}

export function getNumberOfSpaces(string: string): number {
  let nbSpaces = 0;
  const length = string.length;
  for (var index = 0; index < length; index++) {
    if (string[index] === ' ') {
      nbSpaces++;
    }
  }
  return nbSpaces;
}

export function applyCombination(string: string, combination: Array<string>): string {
  const length = combination.length;
  for (let index = 0; index < length; index++) {
    string = string.replace(' ', combination[index]);
  }
  return string;
}

export const extractMrzInformations = (response: string): MrzResult => {
  /**
		To select MRZ lines from result we are making the assumption
		that each has at leat two '<'
	 */
  // $FlowFixMe
  const mrzMatch = response?.match?.(/^.*<{2,}.*$/gm);

  if (!mrzMatch) {
    throw new Error('Impossible to parse MRZ');
  }

  let mrz = mrzMatch.join(LINE_RETURN_KEY);
  mrz = mrz.replace(/«+/g, '<');

  const numberOfSpaces = getNumberOfSpaces(mrz);

  if (numberOfSpaces !== 0) {
    const combinations = generateCombinations(numberOfSpaces);
    const combinationsLength = combinations.length;

    for (let index = 0; index < combinationsLength; index++) {
      try {
        // If we are in this loop it means that result[i] is defined
        // $FlowFixMe
        const newString = applyCombination(mrz, combinations[index]);
        const mrzArray = newString.split(LINE_RETURN_KEY);
        const parsedMrz = parse(mrzArray);
        return parsedMrz;
      } catch (error) {
        WizzeeLog.warn(error);
      }
    }

    throw new Error('Impossible to parse MRZ');
  } else {
    const mrzArray = mrz.split(LINE_RETURN_KEY);
    const parsedMrz = parse(mrzArray);
    return parsedMrz;
  }
};

export const calculateOverlayDimensions = (width: number): { overlayWidth: number, overlayHeight: number } => {
  const overlayWidth = width * 0.9;
  const overlayHeight = overlayWidth * 0.7;

  return { overlayWidth, overlayHeight };
};

type Details = {
  end: number,
  error?: string,
  field: string,
  label: string,
  line: number,
  ranges: string[],
  start: number,
  valid: boolean,
  value: string,
};

type Fields = {
  administrativeCode: string,
  administrativeCode2: string,
  birthDate: string,
  birthDateCheckDigit: string,
  compositeCheckDigit: string,
  documentCode: string,
  documentNumber: string,
  documentNumberCheckDigit: string,
  firstName: string,
  issueDate: string,
  issuingState: string,
  lastName: string,
  sex: string,
};

export type MrzResult = {
  details: Details[],
  fields: Fields,
  format: string,
  valid: boolean,
};

type GCloudResponseType = {
  responses: Array<{
    fullTextAnnotation: GCloudFullTextAnnotation,
  }>,
};

type GCloudFullTextAnnotation = {
  text: string,
};
